import React, { useState, useEffect, useCallback } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { cardBackground, cardBorder } from '../styles/themeHelpers';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import userService from '../services/userService';
import { Player } from '../types/user';
import GameFieldSelector from '../components/common/GameFieldSelector';
import MultiGameFieldSelector from '../components/common/MultiGameFieldSelector';
import { ALL_STATES_OPTION, getStatesWithAllOption } from '../constants/states';
import { GAME_DISPLAY_PREFERENCE_OPTIONS, GAME_DISPLAY_PREFERENCE, GameDisplayPreference } from '../constants/gameDisplayPreference';
import { useNotification } from '../hooks/useNotification';
import NotificationContainer from '../components/common/NotificationContainer';
import MissionHistoryFilters from '../components/mission/MissionHistoryFilters';
import MissionHistoryList from '../components/mission/MissionHistoryList';
import MissionHistoryPagination from '../components/mission/MissionHistoryPagination';
import RanksModal from '../components/rank/RanksModal';
import RankDetailModal from '../components/rank/RankDetailModal';
import missionHistoryService from '../services/missionHistoryService';
import ranksService, { Rank } from '../services/ranksService';
import { MissionHistory, MissionHistoryFilters as MissionHistoryFiltersType } from '../types/missionHistory';
import UserAchievements from '../components/user/UserAchievements';
import clanService from '../services/clanService';
import { ClanRequest } from '../types/clan';
import FieldInfoButton from '../components/field/FieldInfoButton';
import { receivedInvitationsInfo, myApplicationsInfo } from '../components/clan/clanRequestsInfo';

type TabType = 'overview' | 'edit' | 'clan-requests';

interface EditFormData {
  name: string;
  email: string;
  state: string;
  primaryBase: string;
  secondaryBases: string[];
  gameDisplayPreference: GameDisplayPreference;
}

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : 'rgba(0, 0, 0, 0.5)'};
  padding: 20px;
  padding-top: 100px;
  font-family: 'Courier New', monospace;
  
  @media (max-width: 768px) {
    padding-top: 90px;
    padding: 10px;
    padding-top: 90px;
  }
`;

const Content = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    margin-bottom: 25px;
  }
`;

const GameTitle = styled.h1`
  font-size: 28px;
  font-weight: bold;
  color: #00d4ff;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.7);

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-bottom: 30px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  background: ${({ theme }) => theme.name === 'shadowGrid' ? 'rgba(5, 6, 8, 0.6)' : 'transparent'};
  border-radius: ${({ theme }) => theme.name === 'shadowGrid' ? '10px' : '0'};
  padding: ${({ theme }) => theme.name === 'shadowGrid' ? '5px' : '0'};
  border: ${({ theme }) => theme.name === 'shadowGrid'
    ? `1px solid ${theme.colors?.borderLight ?? 'rgba(0, 212, 255, 0.2)'}`
    : '1px solid rgba(0, 212, 255, 0.3)'};
  border-width: ${({ theme }) => theme.name === 'shadowGrid' ? '1px' : '0 0 1px 0'};

  @media (max-width: 768px) {
    margin-bottom: 25px;
    width: 100%;
    -webkit-overflow-scrolling: touch;
  }

  @media (max-width: 480px) {
    margin-bottom: 20px;
    gap: 5px;
  }
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 15px 20px;
  font-size: 1rem;
  font-weight: ${({ $active }) => $active ? 'bold' : 'normal'};
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  flex-shrink: 0;
  white-space: nowrap;
  border-radius: ${({ theme }) => theme.name === 'shadowGrid' ? '8px' : '12px'};
  border: none;
  background: ${({ theme, $active }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $active }) => $active ? '#00121f' : theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Courier New', monospace;

  &:hover {
    background: ${({ theme, $active }) => $active ? theme.colors.primaryDark : theme.colors.surfaceHover};
    color: ${({ theme, $active }) => $active ? '#00121f' : theme.colors.primary};
  }

  @media (max-width: 768px) {
    padding: 12px 15px;
    font-size: 0.85rem;
    flex: 1;
    text-align: center;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
  }

  @media (max-width: 480px) {
    padding: 10px 12px;
    font-size: 0.8rem;
    min-width: 80px;
    gap: 1px;
  }
`;

const TabText = styled.span`
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  .short-text {
    display: none;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    line-height: 1.2;
  }

  @media (max-width: 480px) {
    .full-text {
      display: none;
    }
    
    .short-text {
      display: inline;
    }
  }
`;

const TabContent = styled.div`
  width: 100%;
  min-height: 400px;
`;

const EditSection = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'shadowGrid' ? '16px' : '12px'};
  overflow: hidden;
  margin-bottom: 30px;
  backdrop-filter: none;
  transition: all 0.3s ease;
  box-shadow: ${({ theme }) => theme.name === 'shadowGrid'
    ? '0 20px 45px rgba(0, 0, 0, 0.6), 0 0 28px rgba(0, 212, 255, 0.2)'
    : 'none'};

  ${({ theme }) => theme.name === 'shadowGrid' && css`
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(0, 212, 255, 0.2);
      opacity: 0.25;
      pointer-events: none;
    }
  `}
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label<{ disabled?: boolean }>`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: ${props => props.disabled ? '#666' : '#00d4ff'};
  text-transform: uppercase;
  font-weight: bold;
`;

const Input = styled.input`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  color: #ffffff;
  border-radius: 4px;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 5px rgba(0, 212, 255, 0.3);
  }
  
  &::placeholder {
    color: #666;
  }
`;

const Select = styled.select`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  color: #ffffff;
  border-radius: 4px;
  
  &:focus {
    outline: none;
    border-color: #00d4ff;
    box-shadow: 0 0 5px rgba(0, 212, 255, 0.3);
  }
  
  option {
    background: rgba(0, 0, 0, 0.9);
    color: #ffffff;
  }
`;

const HelpText = styled.p`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #888;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px 24px;
  background: #00d4ff;
  color: #000000;
  border: 2px solid #00d4ff;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 4px;
  
  &:hover {
    background: #00a8cc;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
  }
`;

const CancelButton = styled.button`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px 24px;
  background: rgba(204, 0, 0, 0.1);
  color: #cc0000;
  border: 2px solid #cc0000;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 4px;
  
  &:hover {
    background: rgba(204, 0, 0, 0.2);
    box-shadow: 0 0 10px rgba(204, 0, 0, 0.3);
  }
`;

const Section = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: ${({ theme }) => theme.name === 'shadowGrid' ? '16px' : '12px'};
  overflow: hidden;
  margin-bottom: 30px;
  backdrop-filter: none;
  transition: all 0.3s ease;
  box-shadow: ${({ theme }) => theme.name === 'shadowGrid'
    ? '0 20px 45px rgba(0, 0, 0, 0.6), 0 0 28px rgba(0, 212, 255, 0.2)'
    : 'none'};

  ${({ theme }) => theme.name === 'shadowGrid' && css`
    position: relative;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      border: 1px solid rgba(0, 212, 255, 0.2);
      opacity: 0.25;
      pointer-events: none;
    }

    &:hover {
      border-color: rgba(0, 212, 255, 0.5);
      transform: translateY(-2px);
    }
  `}
`;

const SectionHeader = styled.div`
  background: ${({ theme }) => theme.name === 'shadowGrid'
    ? theme.styles?.headerBackground ?? 'rgba(0, 20, 40, 0.4)'
    : 'transparent'};
  padding: ${({ theme }) => theme.name === 'shadowGrid' ? '15px 20px' : '0'};
  margin-bottom: ${({ theme }) => theme.name === 'shadowGrid' ? '0' : '15px'};
  border-bottom: ${({ theme }) => theme.name === 'shadowGrid'
    ? '1px solid rgba(0, 255, 170, 0.12)'
    : 'none'};
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    margin-bottom: ${({ theme }) => theme.name === 'shadowGrid' ? '0' : '20px'};
  }
`;

const SectionTitle = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  font-size: 1.2rem;
  font-weight: bold;
  text-transform: uppercase;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    text-align: center;
  }
`;

const SectionContent = styled.div`
  padding: ${({ theme }) => theme.name === 'shadowGrid' ? '20px' : '0'};
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const InfoColumn = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff;
  line-height: 1.6;
`;

const InfoLine = styled.div`
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    margin-bottom: 15px;
  }
`;

const Label = styled.span`
  color: #00d4ff;
  text-transform: uppercase;
  font-weight: bold;
`;

const Value = styled.span`
  color: #ffffff;
  margin-left: 10px;
`;

const ClanLink = styled.span`
  color: #00d4ff;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    color: #00a8cc;
  }
`;

const ClanInfo = styled.div`
  margin-bottom: 5px;
`;

const ClanSection = styled.div`
  margin-bottom: 15px;
`;

const ClanRole = styled.span`
  color: #b0b0b0;
  font-weight: normal;
  font-size: 12px;
`;

const RanksTable = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.2);
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: ${props => Array(props.$columns).fill('1fr').join(' ')};
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const TableHeaderCell = styled.div`
  padding: 15px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #00d4ff;
  text-transform: uppercase;
  font-weight: bold;
  background: rgba(0, 212, 255, 0.1);
  border-right: 1px solid rgba(0, 212, 255, 0.2);
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 12px;
  }
`;

const TableRow = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: ${props => Array(props.$columns).fill('1fr').join(' ')};
  border-bottom: 1px solid rgba(0, 212, 255, 0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.div`
  padding: 15px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff;
  border-right: 1px solid rgba(0, 212, 255, 0.1);
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    padding: 12px 8px;
    font-size: 12px;
  }
`;

const FactionTitle = styled.div<{ $faction?: 'human' | 'alien' }>`
  font-size: 16px;
  color: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
  font-weight: bold;
  margin-bottom: 15px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.$faction === 'alien' ? '#ffaa00' : '#60a5fa'};
    text-shadow: 0 0 8px ${props => props.$faction === 'alien' ? 'rgba(255, 136, 0, 0.5)' : 'rgba(59, 130, 246, 0.5)'};
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 18px;
    text-align: center;
  }
`;

const RankInfo = styled.div<{ $faction?: 'human' | 'alien' }>`
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;

  .rank-label {
    color: ${props => props.$faction === 'alien' ? '#ff8800' : '#3b82f6'};
    text-transform: uppercase;
    font-weight: bold;
  }
  
  .rank-value {
    color: ${props => props.$faction === 'alien' ? '#ffcc88' : '#ffffff'};
    margin-left: 10px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      color: ${props => props.$faction === 'alien' ? '#ffaa00' : '#33d4ff'};
      text-shadow: 0 0 6px ${props => props.$faction === 'alien' ? 'rgba(255, 136, 0, 0.5)' : 'rgba(0, 212, 255, 0.5)'};
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  margin: 8px 0;
  
  @media (max-width: 768px) {
    height: 10px;
    margin: 12px 0;
  }
`;

const ProgressFill = styled.div<{ $progress: number; $faction?: 'human' | 'alien' }>`
  height: 100%;
  background: ${props => props.$faction === 'alien' 
    ? 'linear-gradient(90deg, #ff8800, #cc6600)' 
    : 'linear-gradient(90deg, #00d4ff, #0099cc)'};
  width: ${props => props.$progress}%;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: 12px;
  color: #b0b0b0;
  text-align: center;
  margin-top: 4px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const GameTypeSelector = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 20px;
  background: ${({ theme }) => theme.name === 'shadowGrid' ? 'rgba(5, 6, 8, 0.6)' : 'transparent'};
  border-radius: ${({ theme }) => theme.name === 'shadowGrid' ? '999px' : '0'};
  padding: ${({ theme }) => theme.name === 'shadowGrid' ? '4px' : '0'};
  border: ${({ theme }) => theme.name === 'shadowGrid'
    ? `1px solid ${theme.colors?.borderLight ?? 'rgba(0, 212, 255, 0.2)'}`
    : 'none'};
  width: fit-content;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    margin-bottom: 25px;
  }
`;

const GameTypeButton = styled.button<{ $active: boolean }>`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px 24px;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: ${({ $active }) => $active ? 'bold' : 'normal'};
  transition: all 0.3s ease;
  border: none;
  border-radius: 8px;
  background: ${({ theme, $active }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $active }) => $active ? '#00121f' : theme.colors.textSecondary};
  letter-spacing: 1px;

  &:hover {
    background: ${({ theme, $active }) => $active ? theme.colors.primaryDark : theme.colors.surfaceHover};
    color: ${({ theme, $active }) => $active ? '#00121f' : theme.colors.primary};
  }
`;

const RanksDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const RankCard = styled.div`
  background: ${({ theme }) => theme.name === 'shadowGrid'
    ? cardBackground(theme)
    : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${({ theme }) => theme.name === 'shadowGrid'
    ? cardBorder(theme)
    : 'rgba(0, 212, 255, 0.2)'};
  border-radius: ${({ theme }) => theme.name === 'shadowGrid' ? '16px' : '12px'};
  padding: 20px;
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  transition: all 0.3s ease;
  box-shadow: ${({ theme }) => theme.name === 'shadowGrid'
    ? '0 10px 25px rgba(0, 0, 0, 0.4)'
    : 'none'};

  ${({ theme }) => theme.name === 'shadowGrid' && css`
    &:hover {
      border-color: rgba(0, 212, 255, 0.5);
      transform: translateY(-2px);
    }
  `}

  @media (max-width: 768px) {
    padding: 25px;
    border-radius: ${({ theme }) => theme.name === 'shadowGrid' ? '16px' : '15px'};
  }

  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const RankTitle = styled.h3`
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: #00d4ff;
  margin-bottom: 15px;
  text-transform: uppercase;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 20px;
    text-align: center;
  }
`;

const RankHistory = styled.div`
  margin-top: 20px;
`;

const HistoryTitle = styled.h4`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #00d4ff;
  margin-bottom: 10px;
  text-transform: uppercase;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 15px;
    text-align: center;
  }
`;

const HistoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const HistoryItem = styled.li`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #ffffff;
  margin-bottom: 5px;
  padding-left: 10px;
  position: relative;
  
  &:before {
    content: '•';
    color: #00d4ff;
    position: absolute;
    left: 0;
  }
`;


const LoadingSpinner = styled.div`
  text-align: center;
  padding: 60px 20px;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => cardBorder(theme)};
  border-radius: 12px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #ff4444;
  font-size: 18px;
  background: ${({ theme }) => cardBackground(theme)};
  border: 1px solid ${({ theme }) => theme.colors.error};
  border-radius: 12px;
`;

const NoDataMessage = styled.div`
  font-family: 'Courier New', monospace;
  color: #b0b0b0;
  text-align: center;
  padding: 40px 20px;
  font-size: 14px;
  font-style: italic;
  
  @media (max-width: 768px) {
    padding: 50px 25px;
    font-size: 16px;
  }
`;

// Styled components for Clan Requests
const ClanRequestGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const ClanRequestCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(0, 212, 255, 0.4);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 10px;
  }
`;

const ClanRequestHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const ClanRequestTitle = styled.h3`
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: #00d4ff;
  margin: 0;
  cursor: pointer;
  text-transform: uppercase;
  font-weight: bold;
  
  &:hover {
    color: #00a8cc;
    text-decoration: underline;
  }
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ClanRequestDate = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #b0b0b0;
  
  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const ClanRequestInfo = styled.div`
  margin-bottom: 15px;
`;

const ClanRequestDetail = styled.div`
  display: flex;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    margin-bottom: 10px;
  }
`;

const ClanRequestActions = styled.div`
  display: flex;
  gap: 10px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

const AcceptButton = styled.button`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px 16px;
  background: rgba(0, 255, 0, 0.1);
  color: #00ff00;
  border: 1px solid #00ff00;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 4px;
  flex: 1;
  
  &:hover {
    background: rgba(0, 255, 0, 0.2);
    box-shadow: 0 0 5px rgba(0, 255, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 16px;
  }
`;

const DeclineButton = styled.button`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px 16px;
  background: rgba(255, 0, 0, 0.1);
  color: #ff0000;
  border: 1px solid #ff0000;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 4px;
  flex: 1;
  
  &:hover {
    background: rgba(255, 0, 0, 0.2);
    box-shadow: 0 0 5px rgba(255, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 16px;
  }
`;

const WithdrawButton = styled.button`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px 16px;
  background: rgba(255, 165, 0, 0.1);
  color: #ffa500;
  border: 1px solid #ffa500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 4px;
  width: 100%;
  
  &:hover {
    background: rgba(255, 165, 0, 0.2);
    box-shadow: 0 0 5px rgba(255, 165, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 16px;
  }
`;

const ApplicationLimitInfo = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffa500;
  background: rgba(255, 165, 0, 0.1);
  border: 1px solid rgba(255, 165, 0, 0.3);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 18px;
    margin-bottom: 25px;
  }
`;

// Modal styles for withdraw application
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: none;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: #1a232e;
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 212, 255, 0.2);
  animation: scaleIn 0.3s ease-in-out;
  
  @keyframes scaleIn {
    from { 
      opacity: 0;
      transform: scale(0.9);
    }
    to { 
      opacity: 1;
      transform: scale(1);
    }
  }
  
  @media (max-width: 768px) {
    width: 95%;
    margin: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
`;

const ModalTitle = styled.h3`
  font-family: 'Courier New', monospace;
  color: #00d4ff;
  margin: 0;
  font-size: 18px;
  font-weight: bold;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    color: #ff4444;
  }
`;

const ModalBody = styled.div`
  padding: 25px;
  text-align: center;
`;

const WarningIcon = styled.div`
  font-size: 48px;
  margin-bottom: 15px;
`;

const ModalText = styled.p`
  font-family: 'Courier New', monospace;
  color: #ffffff;
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 15px 0;
  
  strong {
    color: #00d4ff;
  }
`;

const ModalSubtext = styled.p`
  font-family: 'Courier New', monospace;
  color: #888;
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 15px;
  padding: 20px 25px;
  border-top: 1px solid rgba(0, 212, 255, 0.2);
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
    gap: 10px;
  }
`;

const CancelModalButton = styled.button`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px 24px;
  background: rgba(136, 136, 136, 0.1);
  color: #888;
  border: 2px solid #888;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 6px;
  flex: 1;
  
  &:hover {
    background: rgba(136, 136, 136, 0.2);
    color: #aaa;
    border-color: #aaa;
    box-shadow: 0 0 10px rgba(136, 136, 136, 0.3);
  }
`;

const WithdrawModalButton = styled.button<{ disabled?: boolean }>`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'rgba(255, 68, 68, 0.5)' : 'rgba(255, 68, 68, 0.1)'};
  color: ${props => props.disabled ? '#ff8888' : '#ff4444'};
  border: 2px solid ${props => props.disabled ? '#ff8888' : '#ff4444'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 6px;
  flex: 1;
  
  &:hover:not(:disabled) {
    background: rgba(255, 68, 68, 0.2);
    box-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
  }
`;

// Component
const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { userId, id } = useParams<{ userId: string; id: string }>();
  const navigate = useNavigate();
  
  // Инициализация состояний с учетом gameDisplayPreference
  const getInitialGameType = (playerData: Player | null): 'airsoft' | 'paintball' => {
    if (!playerData) return 'airsoft';
    
    switch (playerData.gameDisplayPreference) {
      case GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY:
        return 'airsoft';
      case GAME_DISPLAY_PREFERENCE.PAINTBALL_ONLY:
        return 'paintball';
      default:
        return 'airsoft';
    }
  };
  
  const [gameType, setGameType] = useState<'airsoft' | 'paintball'>('airsoft');
  const [missionHistoryGameType, setMissionHistoryGameType] = useState<'airsoft' | 'paintball'>('airsoft');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [playerData, setPlayerData] = useState<Player | null>(null);
  // URL-based tab state for proper back navigation
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TabType) || 'overview';
  const setActiveTab = useCallback((tab: TabType) => {
    setSearchParams({ tab }, { replace: false });
  }, [setSearchParams]);
  const { notifications, showSuccess, showError, removeNotification } = useNotification();
  const theme = useTheme();
  const isShadowGrid = theme.name === 'shadowGrid';

  // Mission History state
  const [missions, setMissions] = useState<MissionHistory[]>([]);
  const [missionHistoryLoading, setMissionHistoryLoading] = useState(false);
  const [missionHistoryError, setMissionHistoryError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [missionHistoryFilters, setMissionHistoryFilters] = useState<MissionHistoryFiltersType>({});
  const pageSize = 10;
  
  // Состояние для модальных окон рангов
  const [ranksModalOpen, setRanksModalOpen] = useState(false);
  const [rankDetailModalOpen, setRankDetailModalOpen] = useState(false);
  const [selectedFaction, setSelectedFaction] = useState<'human' | 'alien'>('human');
  const [selectedRank, setSelectedRank] = useState<any>(null);
  const [humanRanks, setHumanRanks] = useState<Rank[]>([]);
  const [alienRanks, setAlienRanks] = useState<Rank[]>([]);
  

  // Clan requests state
  const [receivedInvitations, setReceivedInvitations] = useState<ClanRequest[]>([]);
  const [userApplications, setUserApplications] = useState<ClanRequest[]>([]);
  const [clanRequestsLoading, setClanRequestsLoading] = useState(false);
  const [clanRequestsError, setClanRequestsError] = useState<string | null>(null);
  
  // Modal states
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [applicationToWithdraw, setApplicationToWithdraw] = useState<ClanRequest | null>(null);

  const currentUserId = userId || id || user?.id;
  const isOwnProfile = user && ((!userId && !id) || userId === user.id || id === user.id);

  // Логика для определения видимости кнопок переключения
  const shouldShowGameTypeSelector = (playerData: Player | null): boolean => {
    if (!playerData) return true;
    return playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.ALL_GAMES;
  };



  const handleClanClick = (clanId: string) => {
    navigate(`/clans/${clanId}`);
  };

  // Обработчики для модальных окон рангов
  const handleFactionClick = (faction: 'human' | 'alien') => {
    setSelectedFaction(faction);
    setRanksModalOpen(true);
  };

  const handleRankClick = (rank: any) => {
    setSelectedRank(rank);
    setRankDetailModalOpen(true);
  };

  const handleRankFromListClick = (rank: any) => {
    setRanksModalOpen(false);
    setSelectedRank(rank);
    setRankDetailModalOpen(true);
  };

  const getCurrentPoints = (faction: 'human' | 'alien') => {
    if (!playerData) return 0;

    return shouldShowGameTypeSelector(playerData)
      ? (gameType === 'airsoft'
          ? (faction === 'human' ? playerData.ranks?.airsoftHumanRank?.points || 0 : playerData.ranks?.airsoftAlienRank?.points || 0)
          : (faction === 'human' ? playerData.ranks?.paintballHumanRank?.points || 0 : playerData.ranks?.paintballAlienRank?.points || 0))
      : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY
          ? (faction === 'human' ? playerData.ranks?.airsoftHumanRank?.points || 0 : playerData.ranks?.airsoftAlienRank?.points || 0)
          : (faction === 'human' ? playerData.ranks?.paintballHumanRank?.points || 0 : playerData.ranks?.paintballAlienRank?.points || 0));
  };

  // Найти ранг по имени из загруженных данных
  const findRankByName = (rankName: string, faction: 'human' | 'alien'): Rank | null => {
    const ranks = faction === 'human' ? humanRanks : alienRanks;
    return ranks.find(r => r.name.toLowerCase() === rankName.toLowerCase()) || null;
  };

  // Получить текущий ранг пользователя с полными данными (включая iconUrl)
  const getCurrentRank = (faction: 'human' | 'alien'): Rank | null => {
    const rankName = shouldShowGameTypeSelector(playerData)
      ? (gameType === 'airsoft'
          ? (faction === 'human' ? playerData?.ranks?.airsoftHumanRank?.rankName : playerData?.ranks?.airsoftAlienRank?.rankName)
          : (faction === 'human' ? playerData?.ranks?.paintballHumanRank?.rankName : playerData?.ranks?.paintballAlienRank?.rankName))
      : (playerData?.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY
          ? (faction === 'human' ? playerData?.ranks?.airsoftHumanRank?.rankName : playerData?.ranks?.airsoftAlienRank?.rankName)
          : (faction === 'human' ? playerData?.ranks?.paintballHumanRank?.rankName : playerData?.ranks?.paintballAlienRank?.rankName));

    if (!rankName) return null;
    return findRankByName(rankName, faction);
  };

  const calculateRankProgress = (currentPoints: number, faction: 'human' | 'alien') => {
    // Basic rank progression calculation
    const rankThresholds = [0, 100, 250, 500, 1000, 2000, 4000, 8000, 15000, 30000];
    
    let currentRankIndex = 0;
    for (let i = 0; i < rankThresholds.length; i++) {
      if (currentPoints >= rankThresholds[i]) {
        currentRankIndex = i;
      } else {
        break;
      }
    }
    
    const nextRankIndex = Math.min(currentRankIndex + 1, rankThresholds.length - 1);
    const nextRankPoints = rankThresholds[nextRankIndex];
    const currentRankPoints = rankThresholds[currentRankIndex];
    
    const progress = nextRankIndex > currentRankIndex 
      ? Math.round(((currentPoints - currentRankPoints) / (nextRankPoints - currentRankPoints)) * 100)
      : 100;
    
    return {
      progress: Math.min(progress, 100),
      nextRankPoints,
      currentRankPoints
    };
  };

  // Mission History functions
  const loadMissionHistory = async (gameType: 'airsoft' | 'paintball', page: number = 1, filters: MissionHistoryFiltersType = {}) => {
    if (!currentUserId) return;

    try {
      setMissionHistoryLoading(true);
      setMissionHistoryError(null);

      const request = {
        userId: currentUserId,
        page,
        pageSize,
        specificDate: filters.specificDate ? missionHistoryService.formatDateForApi(filters.specificDate) : undefined,
        result: filters.result && filters.result !== 'all' ? filters.result : undefined,
        fieldId: filters.fieldId,
        refereeId: filters.refereeId,
        faction: filters.faction && filters.faction !== 'all' ? filters.faction : undefined
      };

      const response = gameType === 'airsoft' 
        ? await missionHistoryService.getUserAirsoftMissionHistory(request)
        : await missionHistoryService.getUserPaintballMissionHistory(request);

      setMissions(response.missions);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
      setHasMore(response.hasMore);
      setCurrentPage(response.page);
    } catch (error) {
      console.error('Error loading mission history:', error);
      setMissionHistoryError('Failed to load mission history');
    } finally {
      setMissionHistoryLoading(false);
    }
  };

  const handleFiltersChange = (filters: MissionHistoryFiltersType) => {
    setMissionHistoryFilters(filters);
  };

  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadMissionHistory(missionHistoryGameType, 1, missionHistoryFilters);
  };

  const handleClearFilters = () => {
    setMissionHistoryFilters({});
    setCurrentPage(1);
    loadMissionHistory(missionHistoryGameType, 1, {});
  };

  const handlePageChange = (page: number) => {
    loadMissionHistory(missionHistoryGameType, page, missionHistoryFilters);
  };

  const handleMissionClick = (mission: MissionHistory) => {
    // Навигируем на страницу детализации миссии
    navigate(`/missions/${mission.gameType}/${mission.id}`);
  };

  // Clan requests functions
  const loadClanRequests = async () => {
    if (!currentUserId || !isOwnProfile) return;

    try {
      setClanRequestsLoading(true);
      setClanRequestsError(null);

      const [invitations, applications] = await Promise.allSettled([
        clanService.getMyInvitations(),
        clanService.getMyApplications()
      ]);

      if (invitations.status === 'fulfilled') {
        setReceivedInvitations(invitations.value);
      } else {
        console.error('Error loading invitations:', invitations.reason);
      }

      if (applications.status === 'fulfilled') {
        setUserApplications(applications.value);
      } else {
        console.error('Error loading applications:', applications.reason);
      }
    } catch (error) {
      console.error('Error loading clan requests:', error);
      setClanRequestsError('Failed to load clan requests');
    } finally {
      setClanRequestsLoading(false);
    }
  };

  const handleAcceptInvitation = async (requestId: string) => {
    try {
      await clanService.acceptInvitation(requestId);
      showSuccess('Invitation Accepted', 'You have successfully joined the clan!', 4000, true);
      loadClanRequests(); // Reload data
    } catch (error) {
      showError('Failed to Accept', 'Could not accept the invitation. Please try again.', 4000, true);
      console.error('Error accepting invitation:', error);
    }
  };

  const handleDeclineInvitation = async (requestId: string) => {
    try {
      await clanService.declineInvitation(requestId);
      showSuccess('Invitation Declined', 'The invitation has been declined.', 4000, true);
      loadClanRequests(); // Reload data
    } catch (error) {
      showError('Failed to Decline', 'Could not decline the invitation. Please try again.', 4000, true);
      console.error('Error declining invitation:', error);
    }
  };

  const handleWithdrawApplication = (requestId: string) => {
    // Найдем заявку для получения информации о клане
    const application = userApplications.find(app => app.id === requestId);
    if (application) {
      setApplicationToWithdraw(application);
      setShowWithdrawModal(true);
    } else {
      showError('Application Error', 'The application could not be found.');
    }
  };

  const confirmWithdrawApplication = async () => {
    if (!applicationToWithdraw) return;
    
    try {
      await clanService.withdrawApplication(applicationToWithdraw.clanId, applicationToWithdraw.userId);
      showSuccess('Application Withdrawn', 'Your application has been successfully withdrawn.', 4000, true);
      setShowWithdrawModal(false);
      setApplicationToWithdraw(null);
      loadClanRequests(); // Reload data
    } catch (error) {
      showError('Withdrawal Failed', 'Could not withdraw the application. Please try again.', 4000, true);
      console.error('Error withdrawing application:', error);
    }
  };

  // Состояние для формы редактирования
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    state: '',
    primaryBase: '',
    secondaryBases: [] as string[],
    gameDisplayPreference: GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY as GameDisplayPreference
  });

  // Инициализация формы редактирования
  useEffect(() => {
    if (playerData && activeTab === 'edit') {
      setEditFormData({
        name: playerData.name || '',
        email: playerData.email || '',
        state: playerData.state || '',
        primaryBase: playerData.primaryBase || '',
        secondaryBases: playerData.secondaryBases || [],
        gameDisplayPreference: (playerData.gameDisplayPreference as GameDisplayPreference) || GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY
      });
    }
  }, [playerData, activeTab]);

  const handleSaveProfile = async () => {
    // Проверяем права доступа
    if (!user || !isOwnProfile || !currentUserId) {
      showError('Unauthorized attempt to save profile', 'error');
      return;
    }

    try {
      const updatedPlayer = await userService.updatePlayerProfile(currentUserId, editFormData);
      setPlayerData(updatedPlayer);
      
      // Если изменилось gameDisplayPreference, обновляем состояния типов игр
      if (editFormData.gameDisplayPreference) {
        const initialGameType = getInitialGameType(updatedPlayer);
        setGameType(initialGameType);
        setMissionHistoryGameType(initialGameType);
      }
      
      showSuccess('Profile updated successfully!', 'success');
      setActiveTab('overview');
    } catch (error) {
      showError('Failed to update profile', 'error');
    }
  };

  const handleEditFormChange = (field: keyof EditFormData, value: string | string[]) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePrimaryBaseChange = (value: string) => {
    setEditFormData(prev => ({ ...prev, primaryBase: value }));
  };

  const handleSecondaryBasesChange = (value: string[]) => {
    setEditFormData(prev => ({ ...prev, secondaryBases: value }));
  };

  const isAllStatesSelected = editFormData.state === ALL_STATES_OPTION || editFormData.state === '';
  const excludedFields = [editFormData.primaryBase, ...editFormData.secondaryBases].filter(Boolean);

  // Загрузка профиля пользователя (только при смене пользователя)
  useEffect(() => {
    const fetchPlayerProfile = async () => {
      if (!currentUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const playerProfile = await userService.getPlayerProfile(currentUserId);
        setPlayerData(playerProfile);
        
        // Обновляем состояния типов игр в зависимости от gameDisplayPreference
        const initialGameType = getInitialGameType(playerProfile);
        setGameType(initialGameType);
        setMissionHistoryGameType(initialGameType);
        
        
        setError('');
      } catch (err) {
        console.error('Error loading player profile:', err);
        setError('Failed to load player profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPlayerProfile();
  }, [currentUserId]);


  // Загрузка Mission History при изменении типа игры или пользователя
  useEffect(() => {
    if (currentUserId && activeTab === 'overview') {
      loadMissionHistory(missionHistoryGameType, 1, {});
    }
  }, [currentUserId, missionHistoryGameType, activeTab]);

  // Загрузка рангов для отображения иконок
  useEffect(() => {
    const loadRanks = async () => {
      try {
        const [human, alien] = await Promise.all([
          ranksService.getRanksByFaction('human'),
          ranksService.getRanksByFaction('alien')
        ]);
        setHumanRanks(human);
        setAlienRanks(alien);
      } catch (error) {
        console.error('Failed to load ranks:', error);
      }
    };
    loadRanks();
  }, []);

  // Load clan requests when switching to clan-requests tab
  useEffect(() => {
    if (currentUserId && activeTab === 'clan-requests' && isOwnProfile) {
      loadClanRequests();
    }
  }, [currentUserId, activeTab, isOwnProfile]);

  if (loading) {
    return (
      <Container>
        <Content>
          <LoadingSpinner>Loading...</LoadingSpinner>
        </Content>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Content>
          <ErrorMessage>{error}</ErrorMessage>
        </Content>
      </Container>
    );
  }

  if (!playerData) {
    return (
      <Container>
        <Content>
          <ErrorMessage>Player not found</ErrorMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <ShadowGridWrapper $enabled={isShadowGrid}>
      <Content>
        <Header>
          <GameTitle>
            {playerData.name.toUpperCase()}
          </GameTitle>
        </Header>

        {/* Game Type Global Selector */}
        {shouldShowGameTypeSelector(playerData) && activeTab === 'overview' && (
          <GameTypeSelector style={{ marginBottom: '30px' }}>
            <GameTypeButton 
              $active={gameType === 'airsoft'}
              onClick={() => {
                setGameType('airsoft');
                setMissionHistoryGameType('airsoft');
              }}
            >
              [Airsoft]
            </GameTypeButton>
            <GameTypeButton 
              $active={gameType === 'paintball'}
              onClick={() => {
                setGameType('paintball');
                setMissionHistoryGameType('paintball');
              }}
            >
              [Paintball]
            </GameTypeButton>
          </GameTypeSelector>
        )}

        {/* Tabs */}
        <TabContainer>
          <Tab 
            $active={activeTab === 'overview'} 
            onClick={() => setActiveTab('overview')}
          >
            <TabText>
              <span className="full-text">Overview</span>
              <span className="short-text">Info</span>
            </TabText>
          </Tab>
          {isOwnProfile && (
            <Tab 
              $active={activeTab === 'edit'} 
              onClick={() => setActiveTab('edit')}
            >
              <TabText>
                <span className="full-text">Edit Profile</span>
                <span className="short-text">Edit</span>
              </TabText>
            </Tab>
          )}
          {isOwnProfile && (
            <Tab 
              $active={activeTab === 'clan-requests'} 
              onClick={() => setActiveTab('clan-requests')}
            >
              <TabText>
                <span className="full-text">Clan Requests</span>
                <span className="short-text">Clans</span>
              </TabText>
            </Tab>
          )}
        </TabContainer>

        <TabContent>
          {activeTab === 'overview' && (
            <>
              {/* OPERATIVE OVERVIEW */}
        <Section>
          <SectionHeader>
            <SectionTitle>Operative Overview</SectionTitle>
          </SectionHeader>
          <SectionContent>
                         <OverviewGrid>
               <InfoColumn>
                 <InfoLine>
                   <Label>CALLSIGN:</Label>
                   <Value>{playerData.name}</Value>
                 </InfoLine>
                 <InfoLine>
                   <Label>MEMBER SINCE:</Label>
                   <Value>{new Date(playerData.createdAt).toLocaleDateString()}</Value>
                 </InfoLine>
                 <InfoLine>
                   <Label>TOTAL GAMES:</Label>
                   <Value>{playerData.gameStatistics.totalGames}</Value>
                 </InfoLine>
                 
                 {/* Показываем статистику в зависимости от gameDisplayPreference */}
                 {shouldShowGameTypeSelector(playerData) ? (
                   // Для ALL_GAMES показываем статистику выбранного типа игры
                   gameType === 'airsoft' ? (
                     <>
                       <InfoLine>
                         <Label>AIRSOFT GAMES:</Label>
                         <Value>{playerData.gameStatistics.totalAirsoftGames}</Value>
                       </InfoLine>
                       <InfoLine>
                         <Label>AIRSOFT WINS:</Label>
                         <Value>{playerData.gameStatistics.airsoftStatistics?.totalWins || 0}</Value>
                       </InfoLine>
                       <InfoLine>
                         <Label>AIRSOFT WIN RATE:</Label>
                         <Value>{(playerData.gameStatistics.airsoftStatistics?.winRate || 0).toFixed(1)}%</Value>
                       </InfoLine>
                     </>
                   ) : gameType === 'paintball' ? (
                     <>
                       <InfoLine>
                         <Label>PAINTBALL GAMES:</Label>
                         <Value>{playerData.gameStatistics.totalPaintballGames}</Value>
                       </InfoLine>
                       <InfoLine>
                         <Label>PAINTBALL WINS:</Label>
                         <Value>{playerData.gameStatistics.paintballStatistics?.totalWins || 0}</Value>
                       </InfoLine>
                       <InfoLine>
                         <Label>PAINTBALL WIN RATE:</Label>
                         <Value>{(playerData.gameStatistics.paintballStatistics?.winRate || 0).toFixed(1)}%</Value>
                       </InfoLine>
                     </>
                   ) : (
                     <>
                       <InfoLine>
                         <Label>SPEEDBALL GAMES:</Label>
                         <Value>{playerData.gameStatistics.totalSpeedballGames}</Value>
                       </InfoLine>
                       <InfoLine>
                         <Label>SPEEDBALL WINS:</Label>
                         <Value>{playerData.gameStatistics.speedballStatistics?.totalWins || 0}</Value>
                       </InfoLine>
                       <InfoLine>
                         <Label>SPEEDBALL WIN RATE:</Label>
                         <Value>{(playerData.gameStatistics.speedballStatistics?.winRate || 0).toFixed(1)}%</Value>
                       </InfoLine>
                     </>
                   )
                 ) : (
                   // Для конкретного типа игры показываем его статистику
                   <>
                     {playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY && (
                       <>
                         <InfoLine>
                           <Label>AIRSOFT GAMES:</Label>
                           <Value>{playerData.gameStatistics.totalAirsoftGames}</Value>
                         </InfoLine>
                         <InfoLine>
                           <Label>AIRSOFT WINS:</Label>
                           <Value>{playerData.gameStatistics.airsoftStatistics?.totalWins || 0}</Value>
                         </InfoLine>
                         <InfoLine>
                           <Label>AIRSOFT WIN RATE:</Label>
                           <Value>{(playerData.gameStatistics.airsoftStatistics?.winRate || 0).toFixed(1)}%</Value>
                         </InfoLine>
                       </>
                     )}
                     {playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.PAINTBALL_ONLY && (
                       <>
                         <InfoLine>
                           <Label>PAINTBALL GAMES:</Label>
                           <Value>{playerData.gameStatistics.totalPaintballGames}</Value>
                         </InfoLine>
                         <InfoLine>
                           <Label>PAINTBALL WINS:</Label>
                           <Value>{playerData.gameStatistics.paintballStatistics?.totalWins || 0}</Value>
                         </InfoLine>
                         <InfoLine>
                           <Label>PAINTBALL WIN RATE:</Label>
                           <Value>{(playerData.gameStatistics.paintballStatistics?.winRate || 0).toFixed(1)}%</Value>
                         </InfoLine>
                       </>
                     )}
                     {playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.SPEEDBALL_ONLY && (
                       <>
                         <InfoLine>
                           <Label>SPEEDBALL GAMES:</Label>
                           <Value>{playerData.gameStatistics.totalSpeedballGames}</Value>
                         </InfoLine>
                         <InfoLine>
                           <Label>SPEEDBALL WINS:</Label>
                           <Value>{playerData.gameStatistics.speedballStatistics?.totalWins || 0}</Value>
                         </InfoLine>
                         <InfoLine>
                           <Label>SPEEDBALL WIN RATE:</Label>
                           <Value>{(playerData.gameStatistics.speedballStatistics?.winRate || 0).toFixed(1)}%</Value>
                         </InfoLine>
                       </>
                     )}
                   </>
                 )}
                 <InfoLine>
                   <Label>CLAN:</Label>
                   {shouldShowGameTypeSelector(playerData) ? (
                     // Показываем клан для выбранного типа игры
                     gameType === 'airsoft' ? (
                       playerData.airsoftClanId && playerData.airsoftClanName ? (
                         <Value>
                           <ClanLink onClick={() => handleClanClick(playerData.airsoftClanId!)}>
                             [{playerData.airsoftClanName}]
                           </ClanLink>
                           {playerData.airsoftClanRole && (
                             <ClanRole> - {playerData.airsoftClanRole}</ClanRole>
                           )}
                         </Value>
                       ) : (
                         <Value> No clan assigned</Value>
                       )
                     ) : (
                       playerData.paintballClanId && playerData.paintballClanName ? (
                         <Value>
                           <ClanLink onClick={() => handleClanClick(playerData.paintballClanId!)}>
                             [{playerData.paintballClanName}]
                           </ClanLink>
                           {playerData.paintballClanRole && (
                             <ClanRole> - {playerData.paintballClanRole}</ClanRole>
                           )}
                         </Value>
                       ) : (
                         <Value> No clan assigned</Value>
                       )
                     )
                   ) : (
                     // Показываем все кланы для пользователей с одним типом игры
                     <ClanSection>
                       {(playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY) && (
                         <ClanInfo>
                           {playerData.airsoftClanId && playerData.airsoftClanName ? (
                             <Value>
                               <ClanLink onClick={() => handleClanClick(playerData.airsoftClanId!)}>
                                 [{playerData.airsoftClanName}]
                               </ClanLink>
                               {playerData.airsoftClanRole && (
                                 <ClanRole> - {playerData.airsoftClanRole}</ClanRole>
                               )}
                             </Value>
                           ) : (
                             <Value> No clan assigned</Value>
                           )}
                         </ClanInfo>
                       )}
                       {(playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.PAINTBALL_ONLY) && (
                         <ClanInfo>
                           {playerData.paintballClanId && playerData.paintballClanName ? (
                             <Value>
                               <ClanLink onClick={() => handleClanClick(playerData.paintballClanId!)}>
                                 [{playerData.paintballClanName}]
                               </ClanLink>
                               {playerData.paintballClanRole && (
                                 <ClanRole> - {playerData.paintballClanRole}</ClanRole>
                               )}
                             </Value>
                           ) : (
                             <Value> No clan assigned</Value>
                           )}
                         </ClanInfo>
                       )}
                     </ClanSection>
                   )}
                 </InfoLine>
               </InfoColumn>
               
               <RanksTable>
                 <TableHeader $columns={1}>
                   <TableHeaderCell>
                     {shouldShowGameTypeSelector(playerData) 
                       ? gameType.charAt(0).toUpperCase() + gameType.slice(1)
                       : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY ? 'Airsoft' : 'Paintball')
                     }
                   </TableHeaderCell>
                 </TableHeader>
                 
                 <TableRow $columns={1}>
                   <TableCell>
                     <FactionTitle 
                       $faction="human" 
                       onClick={() => handleFactionClick('human')}
                       title="Click to view all Human Forces ranks"
                     >
                       Human Forces
                     </FactionTitle>
                     <RankInfo $faction="human">
                       <span className="rank-label">Rank:</span>
                       <span
                         className="rank-value"
                         onClick={() => {
                           const rank = getCurrentRank('human');
                           if (rank) {
                             setSelectedFaction('human');
                             handleRankClick(rank);
                           }
                         }}
                         title="Click to view rank details"
                       >
                         {shouldShowGameTypeSelector(playerData)
                           ? (gameType === 'airsoft'
                               ? playerData.ranks?.airsoftHumanRank?.rankName || 'Not ranked'
                               : playerData.ranks?.paintballHumanRank?.rankName || 'Not ranked')
                           : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY
                               ? playerData.ranks?.airsoftHumanRank?.rankName || 'Not ranked'
                               : playerData.ranks?.paintballHumanRank?.rankName || 'Not ranked')
                         }
                       </span>
                     </RankInfo>
                     <RankInfo $faction="human">
                       <span className="rank-label">Points:</span>
                       <span className="rank-value">
                         {shouldShowGameTypeSelector(playerData) 
                           ? (gameType === 'airsoft' 
                               ? playerData.ranks?.airsoftHumanRank?.points || 0
                               : playerData.ranks?.paintballHumanRank?.points || 0)
                           : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY 
                               ? playerData.ranks?.airsoftHumanRank?.points || 0
                               : playerData.ranks?.paintballHumanRank?.points || 0)
                         }
                       </span>
                     </RankInfo>
                     {((shouldShowGameTypeSelector(playerData) 
                         ? (gameType === 'airsoft' 
                             ? playerData.ranks?.airsoftHumanRank?.points || 0
                             : playerData.ranks?.paintballHumanRank?.points || 0)
                         : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY 
                             ? playerData.ranks?.airsoftHumanRank?.points || 0
                             : playerData.ranks?.paintballHumanRank?.points || 0)
                       ) > 0) && (
                       <>
                         <ProgressBar>
                           <ProgressFill 
                             $progress={(() => {
                               const currentPoints = shouldShowGameTypeSelector(playerData) 
                                 ? (gameType === 'airsoft' 
                                     ? playerData.ranks?.airsoftHumanRank?.points || 0
                                     : playerData.ranks?.paintballHumanRank?.points || 0)
                                 : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY 
                                     ? playerData.ranks?.airsoftHumanRank?.points || 0
                                     : playerData.ranks?.paintballHumanRank?.points || 0);
                               return calculateRankProgress(currentPoints, 'human').progress;
                             })()} 
                             $faction="human" 
                           />
                         </ProgressBar>
                         <ProgressText>
                           {(() => {
                             const currentPoints = shouldShowGameTypeSelector(playerData) 
                               ? (gameType === 'airsoft' 
                                   ? playerData.ranks?.airsoftHumanRank?.points || 0
                                   : playerData.ranks?.paintballHumanRank?.points || 0)
                               : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY 
                                   ? playerData.ranks?.airsoftHumanRank?.points || 0
                                   : playerData.ranks?.paintballHumanRank?.points || 0);
                             const progress = calculateRankProgress(currentPoints, 'human');
                             return `${currentPoints}/${progress.nextRankPoints} points to next rank`;
                           })()}
                         </ProgressText>
                       </>
                     )}
                   </TableCell>
                 </TableRow>
                 
                 <TableRow $columns={1}>
                   <TableCell>
                     <FactionTitle 
                       $faction="alien" 
                       onClick={() => handleFactionClick('alien')}
                       title="Click to view all Alien Alliance ranks"
                     >
                       Alien Alliance
                     </FactionTitle>
                     <RankInfo $faction="alien">
                       <span className="rank-label">Rank:</span>
                       <span
                         className="rank-value"
                         onClick={() => {
                           const rank = getCurrentRank('alien');
                           if (rank) {
                             setSelectedFaction('alien');
                             handleRankClick(rank);
                           }
                         }}
                         title="Click to view rank details"
                       >
                         {shouldShowGameTypeSelector(playerData)
                           ? (gameType === 'airsoft'
                               ? playerData.ranks?.airsoftAlienRank?.rankName || 'Not ranked'
                               : playerData.ranks?.paintballAlienRank?.rankName || 'Not ranked')
                           : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY
                               ? playerData.ranks?.airsoftAlienRank?.rankName || 'Not ranked'
                               : playerData.ranks?.paintballAlienRank?.rankName || 'Not ranked')
                         }
                       </span>
                     </RankInfo>
                     <RankInfo $faction="alien">
                       <span className="rank-label">Points:</span>
                       <span className="rank-value">
                         {shouldShowGameTypeSelector(playerData) 
                           ? (gameType === 'airsoft' 
                               ? playerData.ranks?.airsoftAlienRank?.points || 0
                               : playerData.ranks?.paintballAlienRank?.points || 0)
                           : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY 
                               ? playerData.ranks?.airsoftAlienRank?.points || 0
                               : playerData.ranks?.paintballAlienRank?.points || 0)
                         }
                       </span>
                     </RankInfo>
                     {((shouldShowGameTypeSelector(playerData) 
                         ? (gameType === 'airsoft' 
                             ? playerData.ranks?.airsoftAlienRank?.points || 0
                             : playerData.ranks?.paintballAlienRank?.points || 0)
                         : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY 
                             ? playerData.ranks?.airsoftAlienRank?.points || 0
                             : playerData.ranks?.paintballAlienRank?.points || 0)
                       ) > 0) && (
                       <>
                         <ProgressBar>
                           <ProgressFill 
                             $progress={(() => {
                               const currentPoints = shouldShowGameTypeSelector(playerData) 
                                 ? (gameType === 'airsoft' 
                                     ? playerData.ranks?.airsoftAlienRank?.points || 0
                                     : playerData.ranks?.paintballAlienRank?.points || 0)
                                 : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY 
                                     ? playerData.ranks?.airsoftAlienRank?.points || 0
                                     : playerData.ranks?.paintballAlienRank?.points || 0);
                               return calculateRankProgress(currentPoints, 'alien').progress;
                             })()} 
                             $faction="alien" 
                           />
                         </ProgressBar>
                         <ProgressText>
                           {(() => {
                             const currentPoints = shouldShowGameTypeSelector(playerData) 
                               ? (gameType === 'airsoft' 
                                   ? playerData.ranks?.airsoftAlienRank?.points || 0
                                   : playerData.ranks?.paintballAlienRank?.points || 0)
                               : (playerData.gameDisplayPreference === GAME_DISPLAY_PREFERENCE.AIRSOFT_ONLY 
                                   ? playerData.ranks?.airsoftAlienRank?.points || 0
                                   : playerData.ranks?.paintballAlienRank?.points || 0);
                             const progress = calculateRankProgress(currentPoints, 'alien');
                             return `${currentPoints}/${progress.nextRankPoints} points to next rank`;
                           })()}
                         </ProgressText>
                       </>
                     )}
                   </TableCell>
                 </TableRow>
               </RanksTable>
             </OverviewGrid>
             
             <InfoLine>
               <Label>MAIN BASE:</Label>
               <Value>{playerData.primaryBase || 'No base assigned'}</Value>
             </InfoLine>
             <InfoLine>
               <Label>SECONDARY BASES:</Label>
               <Value>{playerData.secondaryBases?.length ? playerData.secondaryBases.join(', ') : 'No secondary bases'}</Value>
             </InfoLine>
          </SectionContent>
        </Section>


        {/* ACHIEVEMENTS */}
        <Section>
          <SectionHeader>
            <SectionTitle>
              Achievements
              {!shouldShowGameTypeSelector(playerData) && (
                <span style={{ fontSize: '14px', color: '#00d4ff', marginLeft: '10px' }}>
                  [{gameType.toUpperCase()}]
                </span>
              )}
            </SectionTitle>
          </SectionHeader>
          <SectionContent>
            <UserAchievements 
              userId={currentUserId!} 
              gameType={shouldShowGameTypeSelector(playerData) ? 'all' : gameType}
            />
          </SectionContent>
        </Section>

        {/* MISSION HISTORY */}
        <Section>
          <SectionHeader>
            <SectionTitle>
              Mission History
              {!shouldShowGameTypeSelector(playerData) && (
                <span style={{ fontSize: '14px', color: '#00d4ff', marginLeft: '10px' }}>
                  [{missionHistoryGameType.toUpperCase()}]
                </span>
              )}
            </SectionTitle>
          </SectionHeader>
          <SectionContent>
            
            <MissionHistoryFilters
              filters={missionHistoryFilters}
              onFiltersChange={handleFiltersChange}
              onApplyFilters={handleApplyFilters}
              onClearFilters={handleClearFilters}
            />
             
            <MissionHistoryList
              missions={missions}
              loading={missionHistoryLoading}
              error={missionHistoryError}
              onMissionClick={handleMissionClick}
            />

            {!missionHistoryLoading && !missionHistoryError && totalPages > 1 && (
              <MissionHistoryPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                hasMore={hasMore}
                onPageChange={handlePageChange}
              />
            )}
          </SectionContent>
        </Section>
              </>
            )}

            {activeTab === 'edit' && isOwnProfile && (
              <EditProfileContent
                editFormData={editFormData}
                handleEditFormChange={handleEditFormChange}
                handlePrimaryBaseChange={handlePrimaryBaseChange}
                handleSecondaryBasesChange={handleSecondaryBasesChange}
                handleSaveProfile={handleSaveProfile}
                isAllStatesSelected={isAllStatesSelected}
                excludedFields={excludedFields}
                setActiveTab={setActiveTab}
              />
            )}

            {activeTab === 'clan-requests' && isOwnProfile && (
              <ClanRequestsContent
                receivedInvitations={receivedInvitations}
                userApplications={userApplications}
                loading={clanRequestsLoading}
                error={clanRequestsError}
                onAcceptInvitation={handleAcceptInvitation}
                onDeclineInvitation={handleDeclineInvitation}
                onWithdrawApplication={handleWithdrawApplication}
                onClanClick={handleClanClick}
              />
            )}
          </TabContent>
        
        <NotificationContainer 
          notifications={notifications} 
          onRemove={removeNotification} 
        />

        {/* Withdraw Application Modal */}
        {showWithdrawModal && applicationToWithdraw && (
          <ModalOverlay onClick={() => setShowWithdrawModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Withdraw Application</ModalTitle>
                <CloseButton onClick={() => setShowWithdrawModal(false)}>×</CloseButton>
              </ModalHeader>
              <ModalBody>
                <WarningIcon>⚠️</WarningIcon>
                <ModalText>
                  Are you sure you want to withdraw your application to join <strong>{applicationToWithdraw.clanName}</strong>?
                </ModalText>
                <ModalSubtext>
                  This action cannot be undone. You will need to reapply if you change your mind.
                </ModalSubtext>
              </ModalBody>
              <ModalActions>
                <CancelModalButton onClick={() => setShowWithdrawModal(false)}>
                  Keep Application
                </CancelModalButton>
                <WithdrawModalButton 
                  onClick={confirmWithdrawApplication}
                  disabled={clanRequestsLoading}
                >
                  {clanRequestsLoading ? 'Withdrawing...' : 'Withdraw Application'}
                </WithdrawModalButton>
              </ModalActions>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Rank Modals */}
        <RanksModal
          isOpen={ranksModalOpen}
          onClose={() => setRanksModalOpen(false)}
          faction={selectedFaction}
          currentPoints={getCurrentPoints(selectedFaction)}
          onRankClick={handleRankFromListClick}
        />
        
        <RankDetailModal
          isOpen={rankDetailModalOpen}
          onClose={() => setRankDetailModalOpen(false)}
          rank={selectedRank}
          faction={selectedFaction}
          currentPoints={getCurrentPoints(selectedFaction)}
        />
      </Content>
      </ShadowGridWrapper>
    </Container>
  );
};

  // Edit Profile Content Component  
  const EditProfileContent: React.FC<{
    editFormData: EditFormData;
    handleEditFormChange: (field: keyof EditFormData, value: string | string[]) => void;
    handlePrimaryBaseChange: (value: string) => void;
    handleSecondaryBasesChange: (value: string[]) => void;
    handleSaveProfile: () => void;
    isAllStatesSelected: boolean;
    excludedFields: string[];
    setActiveTab: (tab: TabType) => void;
  }> = ({
    editFormData,
    handleEditFormChange,
    handlePrimaryBaseChange,
    handleSecondaryBasesChange,
    handleSaveProfile,
    isAllStatesSelected,
    excludedFields,
    setActiveTab,
  }) => {
    const statesOptions = getStatesWithAllOption();

    return (
      <EditSection>
        <SectionHeader>
          <SectionTitle>Edit Profile</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <EditForm>
            <FormGroup>
              <FormLabel>Name</FormLabel>
              <Input
                type="text"
                value={editFormData.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
                placeholder="Enter your name"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => handleEditFormChange('email', e.target.value)}
                placeholder="Enter your email"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>State</FormLabel>
              <Select
                value={editFormData.state}
                onChange={(e) => handleEditFormChange('state', e.target.value)}
              >
                {statesOptions.map((state) => (
                  <option key={state.value} value={state.value}>
                    {state.abbreviation}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <FormLabel disabled={isAllStatesSelected}>Primary Base</FormLabel>
              <GameFieldSelector
                value={editFormData.primaryBase}
                onChange={handlePrimaryBaseChange}
                clanType="airsoft"
                state={editFormData.state}
                placeholder={isAllStatesSelected ? "Select specific state first" : "Select primary base"}
                disabled={isAllStatesSelected}
                excludeFields={editFormData.secondaryBases}
              />
              {isAllStatesSelected && (
                <HelpText>Please select a specific state to choose your primary base</HelpText>
              )}
            </FormGroup>

            <FormGroup>
              <FormLabel>Secondary Bases</FormLabel>
              <MultiGameFieldSelector
                value={editFormData.secondaryBases}
                onChange={handleSecondaryBasesChange}
                clanType="airsoft"
                state={editFormData.state}
                placeholder="Search and select secondary bases"
                disabled={isAllStatesSelected}
                maxSelections={5}
                excludeFields={excludedFields}
              />
              {isAllStatesSelected && (
                <HelpText>Please select a specific state to choose your secondary bases</HelpText>
              )}
            </FormGroup>

            <FormGroup>
              <FormLabel>Game Display Preference</FormLabel>
              <Select
                value={editFormData.gameDisplayPreference}
                onChange={(e) => handleEditFormChange('gameDisplayPreference', e.target.value)}
              >
                {GAME_DISPLAY_PREFERENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <HelpText>Choose which game types you want to display in your profile</HelpText>
            </FormGroup>

            <ButtonGroup>
              <SaveButton onClick={handleSaveProfile}>
                Save Changes
              </SaveButton>
              <CancelButton onClick={() => setActiveTab('overview')}>
                Cancel
              </CancelButton>
            </ButtonGroup>
          </EditForm>
        </SectionContent>
      </EditSection>
    );
  };

  // Clan Requests Content Component  
  const ClanRequestsContent: React.FC<{
    receivedInvitations: ClanRequest[];
    userApplications: ClanRequest[];
    loading: boolean;
    error: string | null;
    onAcceptInvitation: (requestId: string) => void;
    onDeclineInvitation: (requestId: string) => void;
    onWithdrawApplication: (requestId: string) => void;
    onClanClick: (clanId: string) => void;
  }> = ({
    receivedInvitations,
    userApplications,
    loading,
    error,
    onAcceptInvitation,
    onDeclineInvitation,
    onWithdrawApplication,
    onClanClick,
  }) => {
    if (loading) {
      return (
        <Section>
          <LoadingSpinner>Loading...</LoadingSpinner>
        </Section>
      );
    }

    if (error) {
      return (
        <Section>
          <ErrorMessage>{error}</ErrorMessage>
        </Section>
      );
    }

    return (
      <>
        {/* Received Invitations */}
        <Section>
          <SectionHeader>
            <SectionTitle>Received Invitations</SectionTitle>
            <FieldInfoButton title="Received Invitations Help" sections={receivedInvitationsInfo} />
          </SectionHeader>
          <SectionContent>
            {receivedInvitations.length === 0 ? (
              <NoDataMessage>No invitations received</NoDataMessage>
            ) : (
              <ClanRequestGrid>
                {receivedInvitations.map((invitation) => (
                  <ClanRequestCard key={invitation.id}>
                    <ClanRequestHeader>
                      <ClanRequestTitle 
                        onClick={() => onClanClick(invitation.clanId)}
                      >
                        {invitation.clanName}
                      </ClanRequestTitle>
                      <ClanRequestDate>
                        {new Date(invitation.createdAt).toLocaleDateString()}
                      </ClanRequestDate>
                    </ClanRequestHeader>
                    <ClanRequestInfo>
                      <ClanRequestDetail>
                        <Label>Game Type:</Label>
                        <Value>{invitation.clanType}</Value>
                      </ClanRequestDetail>
                      <ClanRequestDetail>
                        <Label>Status:</Label>
                        <Value style={{ color: '#00d4ff' }}>{invitation.status}</Value>
                      </ClanRequestDetail>
                    </ClanRequestInfo>
                    <ClanRequestActions>
                      <AcceptButton onClick={() => onAcceptInvitation(invitation.id)}>
                        Accept
                      </AcceptButton>
                      <DeclineButton onClick={() => onDeclineInvitation(invitation.id)}>
                        Decline
                      </DeclineButton>
                    </ClanRequestActions>
                  </ClanRequestCard>
                ))}
              </ClanRequestGrid>
            )}
          </SectionContent>
        </Section>

        {/* User Applications */}
        <Section>
          <SectionHeader>
            <SectionTitle>My Applications</SectionTitle>
            <FieldInfoButton title="My Applications Help" sections={myApplicationsInfo} />
          </SectionHeader>
          <SectionContent>
            {userApplications.length === 0 ? (
              <NoDataMessage>No applications sent</NoDataMessage>
            ) : (
              <>
                {/* Info message about application limit */}
                {userApplications.some(app => app.status === 'pending') && (
                  <ApplicationLimitInfo>
                    <Label>Note:</Label> You can only have one active application per game type. 
                    To apply to a different clan, you must first withdraw your current application.
                  </ApplicationLimitInfo>
                )}
                <ClanRequestGrid>
                  {userApplications.map((application) => (
                    <ClanRequestCard key={application.id}>
                      <ClanRequestHeader>
                        <ClanRequestTitle 
                          onClick={() => onClanClick(application.clanId)}
                        >
                          {application.clanName}
                        </ClanRequestTitle>
                        <ClanRequestDate>
                          {new Date(application.createdAt).toLocaleDateString()}
                        </ClanRequestDate>
                      </ClanRequestHeader>
                      <ClanRequestInfo>
                        <ClanRequestDetail>
                          <Label>Game Type:</Label>
                          <Value>{application.clanType}</Value>
                        </ClanRequestDetail>
                        <ClanRequestDetail>
                          <Label>Status:</Label>
                          <Value style={{ 
                            color: application.status === 'pending' ? '#ffa500' : 
                                  application.status === 'accepted' ? '#00ff00' : '#ff0000' 
                          }}>
                            {application.status}
                          </Value>
                        </ClanRequestDetail>
                      </ClanRequestInfo>
                      {application.status === 'pending' && (
                        <ClanRequestActions>
                          <WithdrawButton onClick={() => onWithdrawApplication(application.id)}>
                            Withdraw Application
                          </WithdrawButton>
                        </ClanRequestActions>
                      )}
                    </ClanRequestCard>
                  ))}
                </ClanRequestGrid>
              </>
            )}
          </SectionContent>
        </Section>
      </>
    );
  };

const ShadowGridWrapper = styled.div<{ $enabled: boolean }>`
  /* Стили теперь применяются напрямую в компонентах через theme.name === 'shadowGrid' */
`;

  export default ProfilePage; 
